import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'rgba(14,8,4,0.7)',
  },
  panel: {
    width: '100%',
    maxWidth: '400px',
    maxHeight: '86vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 24px 60px rgba(0,0,0,0.6)',
    ].join(','),
    ...scrollbar,
  },
  back: {
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    color: '#d99a6a',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    padding: '6px 2px',
    '&:hover': { color: '#fff3e6' },
  },
  title: { margin: 0, color: '#f5e6c8', fontSize: '19px', fontWeight: 700 },
  blurb: { margin: 0, color: '#c4a070', fontSize: '13px', lineHeight: 1.55 },
  field: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    color: '#c4a070',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  choices: { display: 'flex', gap: '8px' },
  choice: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minHeight: '46px',
    padding: '8px 6px',
    background: '#241811',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#a8875d',
    textAlign: 'center',
    '&:hover': { color: '#e8d6ae' },
  },
  choiceOn: {
    background: '#3a2416',
    borderColor: '#8b4513',
    color: '#f5e6c8',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -2px 0 rgba(0,0,0,0.3)',
  },
  radio: { position: 'absolute', opacity: 0, width: '1px', height: '1px' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#f5e6c8',
    padding: '11px 13px',
    fontSize: '14px',
    fontFamily: MONO,
    outline: 'none',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color 0.15s',
    '&:focus': { borderColor: '#8b4513' },
    '&::placeholder': { color: '#6a4a2a' },
  },
  hint: { fontSize: '11.5px', color: '#8a6e4c', lineHeight: 1.5 },
  locked: {
    fontSize: '13px',
    color: '#c4a070',
    lineHeight: 1.55,
    background: '#1c130c',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.45)',
  },
  applyBtn: {
    minHeight: '46px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    color: '#fff3e6',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -3px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 2px 5px rgba(0,0,0,0.4)',
    },
  },
});

/**
 * The room's lock and door, changeable at any time — unlike the game mode,
 * which is locked once the game starts. The door is most useful mid-game.
 *
 * Neither change evicts anybody: a seated player is admitted before either
 * gate is consulted, so shutting the door or changing the password can only
 * ever affect somebody who is not here yet.
 */
function RoomAccessModal({ open, access, isHost, onApply, onClose }) {
  const classes = useStyles();
  const t = useT();
  const [visibility, setVisibility] = useState(access.visibility);
  const [password, setPassword] = useState('');
  const [joinable, setJoinable] = useState(access.joinable);

  useModalEscape(open, onClose);

  // Re-seed when the room's policy changes underneath (another host action, or
  // a rejoin), and start the password field empty every time — an empty
  // password means "keep the one you have" server-side, and showing the
  // current one is not possible anyway: the server never sends it.
  useEffect(() => {
    setVisibility(access.visibility);
    setJoinable(access.joinable);
    setPassword('');
  }, [access.visibility, access.joinable, open]);

  if (!open) return null;

  const choice = on => `${classes.choice}${on ? ` ${classes.choiceOn}` : ''}`;
  const alreadyLocked = access.visibility === 'password';

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={t('access.roomAccess')}>
      <div className={classes.panel}>
        <button type="button" className={classes.back} onClick={onClose}>‹ Back to settings</button>
        <h2 className={classes.title}>{t('access.roomAccess')}</h2>
        <p className={classes.blurb}>
          Who can find this room, and whether it is taking new players. Changing
          either affects people who are not here yet — nobody in the room is
          removed.
        </p>

        {!isHost ? (
          <div className={classes.locked}>{t('access.hostOnly')}</div>
        ) : (
          <>
            <div className={classes.field}>
              <span className={classes.label}>{t('access.title')}</span>
              <div className={classes.choices} role="radiogroup" aria-label={t('access.title')}>
                <label className={choice(visibility === 'public')}>
                  <input
                    className={classes.radio}
                    type="radio"
                    name="room-visibility"
                    checked={visibility === 'public'}
                    onChange={() => setVisibility('public')}
                    aria-label={t('access.public')}
                  />
                  {t('access.public')}
                </label>
                <label className={choice(visibility === 'password')}>
                  <input
                    className={classes.radio}
                    type="radio"
                    name="room-visibility"
                    checked={visibility === 'password'}
                    onChange={() => setVisibility('password')}
                    aria-label={t('access.password')}
                  />
                  🔒 Password
                </label>
              </div>
              {visibility === 'password' && (
                <>
                  <input
                    className={classes.input}
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={alreadyLocked ? 'new password' : 'room password'}
                    aria-label={t('a11y.roomPassword')}
                    autoComplete="off"
                  />
                  {alreadyLocked && (
                    <span className={classes.hint}>{t('access.leaveBlank')}</span>
                  )}
                </>
              )}
            </div>

            <div className={classes.field}>
              <span className={classes.label}>{t('access.newPlayers')}</span>
              <div className={classes.choices} role="radiogroup" aria-label={t('access.newPlayers')}>
                <label className={choice(joinable)}>
                  <input
                    className={classes.radio}
                    type="radio"
                    name="room-joinable"
                    checked={joinable}
                    onChange={() => setJoinable(true)}
                    aria-label={t('access.accepting')}
                  />
                  {t('access.accepting')}
                </label>
                <label className={choice(!joinable)}>
                  <input
                    className={classes.radio}
                    type="radio"
                    name="room-joinable"
                    checked={!joinable}
                    onChange={() => setJoinable(false)}
                    aria-label={t('access.notAccepting')}
                  />
                  {t('access.notAccepting')}
                </label>
              </div>
              <span className={classes.hint}>{t('access.seatsKept')}</span>
            </div>

            <button
              type="button"
              className={classes.applyBtn}
              onClick={() => onApply({ visibility, password, joinable })}
            >
              {t('access.apply')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default RoomAccessModal;
